import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import axios from "../../../../api/axios";
import { PlusIcon, PencilIcon, TrashIcon } from "@heroicons/react/24/outline";
import { ReportPriceLineDialog } from "./ReportPriceLineDialog";
import { toast } from "react-hot-toast";

export const ReportPriceLinesSection = ({ reportId, project, className = "" }) => {
  const { t } = useTranslation();
  const [priceLines, setPriceLines] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingPriceLine, setEditingPriceLine] = useState(null);

  useEffect(() => {
    if (reportId) {
      fetchPriceLines();
    }
  }, [reportId]);

  const [reportProject, setReportProject] = useState(project || null);

  useEffect(() => {
    if (project) {
      setReportProject(project);
    } else if (reportId) {
      fetchReportProject();
    }
  }, [project, reportId]);

  const fetchReportProject = async () => {
    try {
      const response = await axios.get(`/service/reports/${reportId}`, {
        params: { fields: "project" }
      });
      setReportProject(response.data?.project || null);
    } catch (err) {
      console.error("Error fetching report project:", err);
      setReportProject(null);
    }
  };

  const fetchPriceLines = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(`/service/reports/${reportId}/price_lines`);
      setPriceLines(response.data || []);
      
      // Also fetch report to get project if not provided as prop
      if (!project) {
        try {
          const reportResponse = await axios.get(`/service/reports/${reportId}`, {
            params: { fields: "project" }
          });
          if (reportResponse.data?.project) {
            setReportProject(reportResponse.data.project);
          }
        } catch (err) {
          console.error("Error fetching report project:", err);
        }
      }
    } catch (err) {
      console.error("Error fetching report price lines:", err);
      setError("Failed to load price lines");
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    setEditingPriceLine(null);
    setDialogOpen(true);
  };

  const handleEdit = (priceLine) => {
    setEditingPriceLine(priceLine);
    setDialogOpen(true);
  };

  const handleDelete = async (priceLineId) => {
    if (!window.confirm(t("report.price_lines.confirm_delete"))) {
      return;
    }

    try {
      await axios.delete(`/service/reports/${reportId}/price_lines/${priceLineId}`);
      
      // Refresh price lines
      fetchPriceLines();
      toast.success(t("report.price_lines.deleted"));
    } catch (err) {
      console.error("Error deleting price line:", err);
      toast.error(t("report.price_lines.delete_error"));
    }
  };

  const handleDialogClose = (saved = false) => {
    setDialogOpen(false);
    setEditingPriceLine(null);
    if (saved) {
      fetchPriceLines();
    }
  };

  const handleDialogSuccess = (newPriceLine) => {
    if (editingPriceLine) {
      // Update existing price line in local state
      setPriceLines((prev) =>
        prev.map((pl) => (pl.id === newPriceLine.id ? newPriceLine : pl))
      );
    } else {
      // Add new price line to local state
      setPriceLines((prev) => [...prev, newPriceLine]);
    }
    setDialogOpen(false);
    setEditingPriceLine(null);
  };

  if (loading) {
    return (
      <div className={`shadow sm:overflow-hidden sm:rounded-md ${className}`}>
        <div className="bg-gradient-to-r from-purple-50 to-indigo-50 px-6 py-4 border-b border-gray-200">
          <div className="flex items-center">
            <div className="animate-pulse flex space-x-4 w-full">
              <div className="rounded-full bg-purple-200 h-10 w-10"></div>
              <div className="flex-1 space-y-2 py-1">
                <div className="h-4 bg-purple-200 rounded w-1/4"></div>
                <div className="h-3 bg-purple-200 rounded w-1/2"></div>
              </div>
            </div>
          </div>
        </div>
        <div className="bg-white px-6 py-6">
          <div className="animate-pulse space-y-4">
            <div className="h-4 bg-gray-200 rounded w-3/4"></div>
            <div className="h-4 bg-gray-200 rounded w-1/2"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`shadow sm:overflow-hidden sm:rounded-md ${className}`}>
        <div className="bg-red-50 px-6 py-4 border-b border-red-200">
          <div className="flex items-center">
            <div className="text-red-600">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div className="ml-4">
              <h3 className="text-lg font-semibold text-red-900">{t("report.price_lines.error_loading")}</h3>
              <p className="text-sm text-red-600">{error}</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className={`shadow sm:overflow-hidden sm:rounded-md ${className}`}>
        <div className="bg-gradient-to-r from-purple-50 to-indigo-50 px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                  <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
              </div>
              <div className="ml-4">
                <div className="flex items-center space-x-3">
                  <h3 className="text-lg font-semibold text-gray-900">{t("report.price_lines.title")}</h3>
                  <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-purple-100 text-purple-800">
                    {priceLines.length}
                  </span>
                </div>
                <p className="text-sm text-gray-600">{t("report.price_lines.description")}</p>
              </div>
            </div>
            <div className="flex space-x-2">
              <button
                onClick={handleAdd}
                className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-purple-600 hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500"
              >
                <PlusIcon className="w-4 h-4 mr-2" />
                {t("report.price_lines.add")}
              </button>
            </div>
          </div>
        </div>
        <div className="bg-white px-6 py-6">
          {priceLines.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-gray-500">{t("report.price_lines.empty")}</p>
              <button
                onClick={handleAdd}
                className="mt-4 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-purple-600 bg-purple-50 hover:bg-purple-100"
              >
                <PlusIcon className="w-4 h-4 mr-2" />
                {t("report.price_lines.add_first")}
              </button>
            </div>
          ) : (
            <div className="space-y-4">
              {priceLines.map((priceLine) => (
                <div
                  key={priceLine.id}
                  className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:bg-gray-50"
                >
                  <div className="flex-1 grid grid-cols-1 md:grid-cols-6 gap-4">
                    <div>
                      <dt className="text-sm font-medium text-gray-500">{t("report.price_lines.title_field")}</dt>
                      <dd className="mt-1 text-sm text-gray-900">
                        {priceLine.title || "-"}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-sm font-medium text-gray-500">{t("report.price_lines.project_activity")}</dt>
                      <dd className="mt-1 text-sm text-gray-900">
                        {priceLine.project_activity?.display_title ||
                          priceLine.project_activity?.title ||
                          priceLine.activity_item?.displayTitle ||
                          t("report.price_lines.no_activity")}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-sm font-medium text-gray-500">{t("report.price_lines.agreed_price_excl_vat")}</dt>
                      <dd className="mt-1 text-sm text-gray-900">
                        {priceLine.agreed_price ? `${Number(priceLine.agreed_price).toFixed(2).replace(".", ",")} kr` : "-"}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-sm font-medium text-gray-500">{t("report.price_lines.agreed_price_incl_vat")}</dt>
                      <dd className="mt-1 text-sm text-gray-900">
                        {priceLine.agreed_price_incl_vat ? `${Number(priceLine.agreed_price_incl_vat).toFixed(2).replace(".", ",")} kr` : "-"}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-sm font-medium text-gray-500">{t("report.price_lines.agreed_hours")}</dt>
                      <dd className="mt-1 text-sm text-gray-900">
                        {priceLine.agreed_hours ? `${priceLine.agreed_hours} h` : "-"}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-sm font-medium text-gray-500">{t("report.price_lines.vat_type")}</dt>
                      <dd className="mt-1 text-sm text-gray-900">
                        {priceLine.vat_type ? `${priceLine.vat_type.display_name || priceLine.vat_type.name} (${priceLine.vat_type.percentage}%)` : "-"}
                      </dd>
                    </div>
                  </div>
                  <div className="ml-4 flex space-x-2">
                    <button
                      onClick={() => handleEdit(priceLine)}
                      className="inline-flex items-center px-2 py-1 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500"
                      title={t("report.price_lines.edit")}
                    >
                      <PencilIcon className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDelete(priceLine.id)}
                      className="inline-flex items-center px-2 py-1 text-sm font-medium text-red-700 bg-white border border-red-300 rounded-md hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                      title={t("report.price_lines.delete")}
                    >
                      <TrashIcon className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <ReportPriceLineDialog
        open={dialogOpen}
        onClose={handleDialogClose}
        reportId={reportId}
        priceLine={editingPriceLine}
        project={reportProject}
        onSuccess={handleDialogSuccess}
      />
    </>
  );
};

